// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the words on an action chip, its menu, its done state and its notes.
// Everything the user reads is a catalog message; the "why" line and "Next
// time" are composed here from the server's structured fields, so the only
// server text shown is the model's own summary of the ask.

import type {
	ActionChipController,
	LearnedChoice,
} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import {
	chipDue,
	chipWhen,
	firstName,
	nameList,
} from '@app/features/konvo_assistant/components/action_chip/state/ChipFormat';
import type {ChoiceKey, SuggestionWhy} from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {pillTimeText, shortTimeText} from '@app/features/konvo_mail/state/MailMeeting';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const T = {
	taskFor: msg({
		message: 'Task for {name}',
		comment: 'Action chip label: create a ClickUp task assigned to this person (their first name).',
	}),
	taskForYou: msg({
		message: 'Task for you',
		comment: 'Action chip label: create a ClickUp task assigned to the reader.',
	}),
	remindMe: msg({message: 'Remind me', comment: 'Action chip label: set a reminder for the reader.'}),
	eventWith: msg({
		message: 'Event with {names}',
		comment: 'Action chip label: a meeting with these people (first names joined, e.g. "Gary and Engen").',
	}),
	createEventWith: msg({
		message: 'Create event with {names}',
		comment: 'Action chip menu item: create a calendar event that invites these people (first names joined).',
	}),
	blockTime: msg({
		message: 'Block time',
		comment: 'Action chip label and button: put a busy block in the reader’s own calendar to do the work.',
	}),
	blockTimeToDoIt: msg({
		message: 'Block time to do it',
		comment: 'Action chip menu item: a busy block in the reader’s own calendar, no invites.',
	}),
	justYou: msg({
		message: 'Just you · {when}',
		comment:
			'Action chip menu: under "Block time to do it"; the block has no other attendees. {when} is e.g. "Thu 14:00–14:30".',
	}),
	replyWithATime: msg({
		message: 'Reply with a time',
		comment:
			'Action chip label on a chat message that asks when to meet: pick a time from your calendar and reply with it.',
	}),
	pickTime: msg({
		message: 'Pick a time',
		comment:
			'Action chip: heading of the time picker (and the button that opens it) for a message that asks when to meet.',
	}),
	replyWith: msg({
		message: 'Reply with {time}',
		comment:
			'Action chip time picker button: puts a reply with this time in the chat’s message box (not sent). {time} is e.g. "Tue 11:00".',
	}),
	holdTime: msg({
		message: 'Hold {time}',
		comment:
			'Action chip time picker button: blocks this time in the reader’s own calendar (no invites). {time} is e.g. "Tue 11:00".',
	}),
	replyReady: msg({
		message: 'Reply with {when} is in the message box',
		comment:
			'Action chip done state after "Reply with <time>": the reply is in the chat’s message box, ready to send. {when} is e.g. "Tue 22 Sep, 11:00–12:00".',
	}),
	held: msg({
		message: 'Held {when}',
		comment:
			'Action chip done state after "Hold <time>": the time is blocked in the calendar, e.g. "Held Tue 22 Sep, 11:00–12:00".',
	}),
	nothing: msg({
		message: 'Nothing to do here',
		comment: 'Action chip menu item: no action for this message; Konvo offers less like it next time.',
	}),
	nothingMeta: msg({
		message: 'Konvo learns from this',
		comment: 'Action chip menu: under "Nothing to do here".',
	}),
	konvoCan: msg({message: 'Konvo can', comment: 'Heading of the action chip menu, above the actions it can take.'}),
	inList: msg({
		message: 'in {list}',
		comment: 'Action chip: the ClickUp list the task goes in, after "Task for Markus", e.g. "in iCAM Ticketing".',
	}),
	due: msg({
		message: 'due {when}',
		comment: 'Action chip: when the task is due, e.g. "due Fri" or "due tomorrow 09:00".',
	}),
	changeAction: msg({
		message: 'Change action',
		comment: 'Tooltip on an action chip label (with its chevron) that opens the list of other actions.',
	}),
	openForm: msg({
		message: 'Edit before creating',
		comment:
			'Action chip menu item: opens the full prefilled form for the current action (task panel, reminder, event).',
	}),
	createTask: msg({message: 'Create task', comment: 'Action chip button: creates the task right away.'}),
	create: msg({
		message: 'Create',
		comment:
			'Action chip button on phones (short): does the offered action right away (task, reminder, event, time block).',
	}),
	setReminder: msg({message: 'Set reminder', comment: 'Action chip button: sets the reminder right away.'}),
	createEvent: msg({message: 'Create event', comment: 'Action chip button: opens the prefilled new-event card.'}),
	dismiss: msg({
		message: 'Dismiss suggestion',
		comment: 'Accessible label of the close control on an assistant suggestion.',
	}),
	// Done
	taskCreatedFor: msg({
		message: 'Task created for {name}',
		comment: 'Action chip done state after creating a ClickUp task for this person (first name).',
	}),
	taskCreatedForYou: msg({
		message: 'Task created for you',
		comment: 'Action chip done state after creating a ClickUp task assigned to the reader.',
	}),
	reminderSet: msg({
		message: 'Reminder set for {when}',
		comment: 'Action chip done state after setting a reminder, e.g. "Reminder set for Fri 09:00".',
	}),
	timeBlocked: msg({
		message: 'Time blocked {when}',
		comment: 'Action chip done state after adding a busy block to the calendar, e.g. "Time blocked Thu 14:00–14:30".',
	}),
	eventCreated: msg({
		message: 'Event created',
		comment: 'Action chip done state after the event card created the event.',
	}),
	open: msg({message: 'Open', comment: 'Action chip done state: opens the task just created.'}),
	undo: msg({message: 'Undo', comment: 'Action chip done state: deletes what was just created.'}),
	// Why (J5): "<Remind me> was picked because you chose it for Gary's last 3 asks."
	whySender: msg({
		message: 'was picked because you chose it for {name}’s last {count} asks.',
		comment:
			'Action chip menu, first line, after the action in bold (e.g. "Remind me"): Konvo picked it from the reader’s habit with this person. {count} is 2 or more.',
	}),
	whyChat: msg({
		message: 'was picked because you chose it for the last {count} asks in {name}.',
		comment:
			'Action chip menu, first line, after the action in bold: Konvo picked it from the reader’s habit in this chat. {count} is 2 or more.',
	}),
	whyDomain: msg({
		message: 'was picked because you chose it for the last {count} asks from {name}.',
		comment:
			'Action chip menu, first line, after the action in bold: Konvo picked it from the reader’s habit with this company. {count} is 2 or more.',
	}),
	// Next time (J6)
	nextSender: msg({
		message: 'Next time, {name}’s asks start as {action}',
		comment:
			'Note after the reader switched the action: Konvo will offer this first for this person. {action} is e.g. "a task".',
	}),
	nextChat: msg({
		message: 'Next time, asks in {name} start as {action}',
		comment:
			'Note after the reader switched the action: Konvo will offer this first in this chat. {action} is e.g. "a task".',
	}),
	nextDomain: msg({
		message: 'Next time, asks from {name} start as {action}',
		comment:
			'Note after the reader switched the action: Konvo will offer this first for this company. {action} is e.g. "a task".',
	}),
	quietSender: msg({
		message: 'Konvo will offer less for {name}’s asks',
		comment: 'Note after "Nothing to do here" taught Konvo about this person.',
	}),
	quietChat: msg({
		message: 'Konvo will offer less for asks in {name}',
		comment: 'Note after "Nothing to do here" taught Konvo about this chat.',
	}),
	quietDomain: msg({
		message: 'Konvo will offer less for asks from {name}',
		comment: 'Note after "Nothing to do here" taught Konvo about this company.',
	}),
	aTask: msg({message: 'a task', comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".'}),
	aTaskForYou: msg({
		message: 'a task for you',
		comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".',
	}),
	aReminder: msg({message: 'a reminder', comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".'}),
	anEvent: msg({message: 'an event', comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".'}),
	blockedTime: msg({message: 'blocked time', comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".'}),
	aTimeReply: msg({
		message: 'a reply with a time',
		comment: 'Fills {action} in "Next time, Gary’s asks start as {action}".',
	}),
	// Sent notice (D1)
	sentTo: msg({
		message: 'Sent to {name}',
		comment: 'Notice after sending a message that asked someone for something; {name} is the chat.',
	}),
	sent: msg({message: 'Sent', comment: 'Notice after sending a message that asked someone for something.'}),
} satisfies Record<string, MessageDescriptor>;

/** The last part of a ClickUp list's path ("iCAM / Support / iCAM Ticketing" → "iCAM Ticketing"). */
export function listName(listId: string | null | undefined): string | null {
	if (!listId) return null;
	const list = KonvoClickUp.lists.find((entry) => entry.id === listId);
	if (!list) return null;
	const parts = list.path.split(' / ');
	return parts[parts.length - 1] || list.name;
}

function taskListId(chip: ActionChipController, key: 'task' | 'task_me'): string | null {
	const options = chip.options;
	return options[key]?.draft?.list_id ?? options.task?.draft?.list_id ?? options.task_me?.draft?.list_id ?? null;
}

/** "Task for Markus", "Remind me", "Event with Gary". */
export function chipLabel(i18n: I18n, chip: ActionChipController, key: ChoiceKey = chip.choice): string {
	const options = chip.options;
	switch (key) {
		case 'task':
			return i18n._(T.taskFor, {name: firstName(options.task?.assignee.name)});
		case 'task_me':
			return i18n._(T.taskForYou);
		case 'reminder':
			return i18n._(T.remindMe);
		case 'event':
			return i18n._(T.eventWith, {
				names: nameList(
					(options.event?.attendees ?? []).map((person) => person.name || person.address),
					getCurrentLocale(),
				),
			});
		case 'block_time':
			return i18n._(T.blockTime);
		case 'meeting':
			return i18n._(T.replyWithATime);
		default:
			return i18n._(T.nothing);
	}
}

/** The quiet part after the label: "in iCAM Ticketing", "due tomorrow 09:00 · iCAM Ticketing", "Fri 09:00". */
export function chipMeta(i18n: I18n, chip: ActionChipController, key: ChoiceKey = chip.choice): string | null {
	const locale = getCurrentLocale();
	const options = chip.options;
	if (key === 'task' || key === 'task_me') {
		const option = options[key];
		const list = listName(taskListId(chip, key));
		const due = option?.due ? chipDue(option.due, locale) : '';
		if (due) return [i18n._(T.due, {when: due}), list].filter(Boolean).join(' · ');
		return list ? i18n._(T.inList, {list}) : null;
	}
	if (key === 'reminder' && options.reminder) return chipWhen(options.reminder.remind_at, null, locale);
	if (key === 'event' && options.event) return chipWhen(options.event.start, options.event.end, locale);
	if (key === 'block_time' && options.block_time) {
		return chipWhen(options.block_time.start, options.block_time.end, locale);
	}
	if (key === 'meeting') {
		const option = chip.meetingOption;
		return option ? shortTimeText(option.start, locale) : null;
	}
	return null;
}

/** The second line of a menu item. */
export function menuMeta(i18n: I18n, chip: ActionChipController, key: ChoiceKey): string | null {
	const locale = getCurrentLocale();
	const options = chip.options;
	if (key === 'task' || key === 'task_me') {
		const option = options[key];
		const due = option?.due ? i18n._(T.due, {when: chipDue(option.due, locale)}) : null;
		return [listName(taskListId(chip, key)), due].filter(Boolean).join(' · ') || null;
	}
	if (key === 'block_time' && options.block_time) {
		return i18n._(T.justYou, {when: chipWhen(options.block_time.start, options.block_time.end, locale)});
	}
	if (key === 'none') return i18n._(T.nothingMeta);
	return chipMeta(i18n, chip, key);
}

/** The menu's wording for an action (events and block time are spelled out there). */
export function menuLabel(i18n: I18n, chip: ActionChipController, key: ChoiceKey): string {
	if (key === 'event') {
		return i18n._(T.createEventWith, {
			names: nameList(
				(chip.options.event?.attendees ?? []).map((person) => person.name || person.address),
				getCurrentLocale(),
			),
		});
	}
	if (key === 'block_time') return i18n._(T.blockTimeToDoIt);
	return chipLabel(i18n, chip, key);
}

/** The line under the label in the composer strip: what will be created. */
export function chipDetail(chip: ActionChipController, key: ChoiceKey = chip.choice): string {
	const options = chip.options;
	switch (key) {
		case 'task':
		case 'task_me':
			return options[key]?.name ?? chip.suggestion.summary;
		case 'reminder':
			return options.reminder?.note || chip.suggestion.summary;
		case 'event':
			return options.event?.title ?? chip.suggestion.summary;
		case 'block_time':
			return options.block_time?.title ?? chip.suggestion.summary;
		case 'meeting':
			return options.meeting?.topic ?? chip.suggestion.summary;
		default:
			return chip.suggestion.summary;
	}
}

/** The button that acts: "Create task", "Set reminder", "Create event", "Block time". */
export function actionLabel(i18n: I18n, key: ChoiceKey): string {
	switch (key) {
		case 'task':
		case 'task_me':
			return i18n._(T.createTask);
		case 'reminder':
			return i18n._(T.setReminder);
		case 'event':
			return i18n._(T.createEvent);
		case 'meeting':
			return i18n._(T.pickTime);
		default:
			return i18n._(T.blockTime);
	}
}

/** "Task created for Markus", "Reminder set for Fri 09:00". */
export function doneTitle(i18n: I18n, chip: ActionChipController): string {
	const key = chip.created?.key ?? chip.choice;
	const locale = getCurrentLocale();
	const options = chip.options;
	switch (key) {
		case 'task':
			return i18n._(T.taskCreatedFor, {name: firstName(options.task?.assignee.name)});
		case 'task_me':
			return i18n._(T.taskCreatedForYou);
		case 'reminder':
			return i18n._(T.reminderSet, {
				when: options.reminder ? chipWhen(options.reminder.remind_at, null, locale) : '',
			});
		case 'block_time':
			return i18n._(T.timeBlocked, {
				when: options.block_time ? chipWhen(options.block_time.start, options.block_time.end, locale) : '',
			});
		case 'meeting': {
			const done = chip.meetingDone;
			const when = done ? pillTimeText(done, locale) : '';
			return done?.mode === 'hold' ? i18n._(T.held, {when}) : i18n._(T.replyReady, {when});
		}
		default:
			return i18n._(T.eventCreated);
	}
}

/** The quiet line of the done state: the list for a task, else what was created. */
export function doneMeta(chip: ActionChipController): string {
	const key = chip.created?.key ?? chip.choice;
	if (key === 'task' || key === 'task_me') {
		return [listName(chip.created?.listId ?? taskListId(chip, key)), chipDetail(chip, key)].filter(Boolean).join(' · ');
	}
	return chipDetail(chip, key);
}

/** The rest of the why line after the action in bold. */
export function whyText(i18n: I18n, why: SuggestionWhy): string {
	const values = {count: why.count, name: why.scope === 'sender' ? firstName(why.scope_name) : why.scope_name};
	if (why.scope === 'chat') return i18n._(T.whyChat, values);
	if (why.scope === 'domain') return i18n._(T.whyDomain, values);
	return i18n._(T.whySender, values);
}

function actionPhrase(i18n: I18n, key: ChoiceKey): string {
	switch (key) {
		case 'task':
			return i18n._(T.aTask);
		case 'task_me':
			return i18n._(T.aTaskForYou);
		case 'reminder':
			return i18n._(T.aReminder);
		case 'event':
			return i18n._(T.anEvent);
		case 'meeting':
			return i18n._(T.aTimeReply);
		default:
			return i18n._(T.blockedTime);
	}
}

/** "Next time, Gary’s asks start as a task", or for "Nothing to do here" "Konvo will offer less for Gary’s asks". */
export function learnedText(i18n: I18n, learned: LearnedChoice): string {
	const scope = learned.scope.split(':')[0];
	const name = scope === 'sender' ? firstName(learned.scope_name) : learned.scope_name;
	if (learned.chosen === 'none') {
		if (scope === 'chat') return i18n._(T.quietChat, {name});
		if (scope === 'domain') return i18n._(T.quietDomain, {name});
		return i18n._(T.quietSender, {name});
	}
	const action = actionPhrase(i18n, learned.chosen);
	if (scope === 'chat') return i18n._(T.nextChat, {name, action});
	if (scope === 'domain') return i18n._(T.nextDomain, {name, action});
	return i18n._(T.nextSender, {name, action});
}
